import { isDefined } from '@sgme/fp';
import {
  WithDragAndDrop,
  WithDraggable,
} from 'components/shared/DragAndDropWorkspace/TabsWorkspace/dragndrop/DragAndDrop';
import { TabsListLayout, WorkspaceLayout } from 'components/shared/DragAndDropWorkspace/TabsWorkspace/Layout';
import { RightMenu } from 'components/shared/DragAndDropWorkspace/TabsWorkspace/RightMenu';
import { FC, useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useIntersectionObserver } from 'usehooks-ts';

import { DashboardTab } from './Tab/DashboardTab';
import { IconButton } from './Tab/IconButton';
import { Tab } from './Tab/Tab';
import type { TabsWorkspaceProps } from './TabsWorkspace.connect';

export const TabsWorkspace: FC<TabsWorkspaceProps> = ({
  paddingClassName,
  workspace,
  isDashboardActive,
  tabs,
  selectedTab,
  addNewTabTooltip,
  setSelectedTab,
  onClose,
  children,
  onReset,
  moveTab,
  onRename,
  onAddNewTab,
  onImportExcel,
  onHideRunningQuotes,
  onShowRunningQuotes,
  e2e,
}) => {
  const { formatMessage } = useIntl();

  const { isIntersecting, ref: refAddTab } = useIntersectionObserver({ threshold: 0.9 });
  const isAddButtonVisible = isIntersecting;

  const makeSureDashboardIsHidden = useCallback(() => {
    if (isDashboardActive === 'show' && onHideRunningQuotes !== undefined) {
      onHideRunningQuotes();
    }
  }, [isDashboardActive, onHideRunningQuotes]);

  const onTabSelected = useCallback(
    (tab: any) => {
      makeSureDashboardIsHidden();
      if (selectedTab?.id !== tab?.id) {
        setSelectedTab(tab);
      }
    },
    [makeSureDashboardIsHidden, setSelectedTab, selectedTab],
  );

  const onTabCreated = useCallback(() => {
    makeSureDashboardIsHidden();
    onAddNewTab!();
  }, [makeSureDashboardIsHidden, onAddNewTab]);

  const addTabTooltip = formatMessage({ id: addNewTabTooltip ?? 'workspace.addNewTab' });
  const tabContent = !isDefined(children) ? null : children(selectedTab, isDashboardActive, onHideRunningQuotes);
  const showDashboardTab = onHideRunningQuotes !== undefined && onShowRunningQuotes !== undefined;

  const dashboardTab = useMemo(
    () =>
      showDashboardTab && (
        <DashboardTab
          e2e="dashboard-toggle-button"
          isActive={isDashboardActive}
          onHide={onHideRunningQuotes}
          onShow={onShowRunningQuotes}
          isDisabled={false}
        />
      ),
    [showDashboardTab, isDashboardActive, onHideRunningQuotes, onShowRunningQuotes],
  );

  const rightMenu = useMemo(
    () => (
      <RightMenu
        showAddButton={onAddNewTab !== undefined && !isAddButtonVisible}
        hasSelectedTab={selectedTab !== null}
        onTabCreated={onTabCreated}
        onReset={onReset}
        onImportExcel={onImportExcel}
        setSelectedTab={setSelectedTab}
        tabs={tabs}
        workspace={workspace}
        addTabTooltip={addTabTooltip}
      />
    ),
    [
      onAddNewTab,
      isAddButtonVisible,
      selectedTab,
      onTabCreated,
      onReset,
      onImportExcel,
      setSelectedTab,
      tabs,
      workspace,
      addTabTooltip,
    ],
  );

  const addButton = onAddNewTab !== undefined && (
    <IconButton
      ref={refAddTab}
      icon="add"
      active={selectedTab === null}
      onClick={onTabCreated}
      e2e="add-new-tab"
      tooltip={addTabTooltip}
    />
  );

  const enableDragAndDrop = tabs.length > 1;

  return (
    <WorkspaceLayout
      dashboardTab={dashboardTab}
      rightMenu={rightMenu}
      paddingClassName={paddingClassName}
      e2e={e2e}
      tabContent={tabContent}
    >
      <WithDragAndDrop moveTab={moveTab} enableDragAndDrop={enableDragAndDrop}>
        <TabsListLayout tabs={tabs} workspace={workspace} addButton={addButton}>
          {(tab, idx) => {
            const isActive = selectedTab?.id === tab.id && isDashboardActive !== 'show';
            return (
              <WithDraggable
                key={tab.id}
                id={tab.id}
                blockIndex={idx}
                enableDragAndDrop={enableDragAndDrop}
                isActive={isActive}
              >
                <Tab
                  key={tab.id}
                  tab={tab}
                  onTabSelected={onTabSelected}
                  isBackground={isDashboardActive === 'show'}
                  isSelected={isActive}
                  onClose={onClose}
                  onTabNameChanged={onRename}
                  workspaceType={workspace}
                />
              </WithDraggable>
            );
          }}
        </TabsListLayout>
      </WithDragAndDrop>
    </WorkspaceLayout>
  );
};

TabsWorkspace.displayName = 'TabsWorkspace';
